import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { User, Shield, Settings as SettingsIcon, Wallet, ShieldCheck, Loader2, Copy, CheckCircle2, XCircle, Unplug, Link2, ExternalLink } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useWallet } from '@/lib/wallet-context';
import { authAPI, web3API } from '@/lib/api';
import { ConnectWalletModal } from '@/components/ConnectWalletModal';
import { toast } from 'sonner';

const CHAIN_INFO = {
  ethereum: { name: 'Ethereum', color: '#627EEA', explorer: 'https://etherscan.io/address/' },
  bsc: { name: 'BSC', color: '#F0B90B', explorer: 'https://bscscan.com/address/' },
  polygon: { name: 'Polygon', color: '#8247E5', explorer: 'https://polygonscan.com/address/' },
  tron: { name: 'TRON', color: '#FF0013', explorer: 'https://tronscan.org/#/address/' },
};

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { t, lang } = useLanguage();
  const { disconnect: disconnectWalletContext } = useWallet();
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [setupLoading, setSetupLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  // Wallet states
  const [connectedWallets, setConnectedWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [unlinkingChain, setUnlinkingChain] = useState(null);

  const load2FAStatus = useCallback(async () => {
    try {
      const res = await authAPI.get2FAStatus();
      setTwoFaStatus(res.data);
    } catch (err) {
      console.error('2FA status error:', err);
    }
  }, []);

  const loadConnectedWallets = useCallback(async () => {
    setWalletsLoading(true);
    try {
      const res = await web3API.getConnectedWallets();
      setConnectedWallets(res.data.wallets || []);
    } catch (err) {
      console.error('Connected wallets error:', err);
    } finally {
      setWalletsLoading(false);
    }
  }, []);

  useEffect(() => {
    load2FAStatus();
    loadConnectedWallets();
  }, [load2FAStatus, loadConnectedWallets]);

  const handleSetup2FA = async () => {
    setSetupLoading(true);
    try {
      const res = await authAPI.setup2FA();
      setSetupData(res.data);
      setShowSetup(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verifyCode || verifyCode.length < 6) return;
    setVerifyLoading(true);
    try {
      const res = await authAPI.verify2FA({ code: verifyCode });
      setBackupCodes(res.data.backup_codes || []);
      setShowSetup(false);
      setSetupData(null);
      setVerifyCode('');
      toast.success(t('settings.twoFaEnableSuccess'));
      await load2FAStatus();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableCode) return;
    setDisableLoading(true);
    try {
      await authAPI.disable2FA({ code: disableCode });
      setShowDisable(false);
      setDisableCode('');
      setBackupCodes([]);
      toast.success(t('settings.twoFaDisableSuccess'));
      await load2FAStatus();
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setDisableLoading(false);
    }
  };

  const handleWalletLinked = async (walletInfo) => {
    try {
      await web3API.linkWallet({
        wallet_address: walletInfo.address,
        wallet_type: walletInfo.wallet_type,
        chain: walletInfo.chain,
        signature: walletInfo.signature,
        challenge: walletInfo.challenge,
      });
      toast.success(t('settings.walletLinked'));
      setWalletModalOpen(false);
      disconnectWalletContext();
      await loadConnectedWallets();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.walletLinkFailed'));
    }
  };

  const handleUnlinkWallet = async (chain) => {
    setUnlinkingChain(chain);
    try {
      await web3API.unlinkWallet(chain);
      toast.success(t('settings.walletUnlinked'));
      await loadConnectedWallets();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('settings.walletUnlinkFailed'));
    } finally {
      setUnlinkingChain(null);
    }
  };

  const is2FAEnabled = twoFaStatus?.enabled || false;

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(lang, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <DashboardLayout title={t('settings.title')}>
      <div className="max-w-2xl space-y-6">
        <Tabs defaultValue="profile">
          <TabsList className="bg-[hsl(var(--secondary))]">
            <TabsTrigger value="profile" data-testid="settings-profile-tab" className="data-[state=active]:bg-[hsl(var(--card))]">
              <User className="w-4 h-4 mr-2" /> {t('settings.profileTab')}
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="settings-security-tab" className="data-[state=active]:bg-[hsl(var(--card))]">
              <Shield className="w-4 h-4 mr-2" /> {t('settings.securityTab')}
            </TabsTrigger>
            <TabsTrigger value="wallets" data-testid="settings-wallets-tab" className="data-[state=active]:bg-[hsl(var(--card))]">
              <Wallet className="w-4 h-4 mr-2" /> {t('settings.walletsTab')}
            </TabsTrigger>
            <TabsTrigger value="preferences" data-testid="settings-preferences-tab" className="data-[state=active]:bg-[hsl(var(--card))]">
              <SettingsIcon className="w-4 h-4 mr-2" /> {t('settings.preferencesTab')}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk']">{t('settings.profileTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('settings.fullName')}</Label>
                  <Input defaultValue={user?.name || ''} className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.email')}</Label>
                  <Input defaultValue={user?.email || ''} disabled className="bg-[hsl(var(--background))] border-[hsl(var(--border))] opacity-60" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.role')}</Label>
                  <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]">
                    {user?.role === 'admin' ? t('settings.admin') : t('settings.user')}
                  </Badge>
                </div>
                <Separator className="bg-[hsl(var(--border))]" />
                <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90">
                  {t('common.save')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk']">{t('settings.securityTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* 2FA Status */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('settings.twoFa')}</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('settings.twoFaDesc')}</p>
                  </div>
                  <Badge data-testid="settings-2fa-badge" className={
                    is2FAEnabled
                      ? 'bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20'
                      : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
                  }>
                    {is2FAEnabled ? (
                      <><CheckCircle2 className="w-3 h-3 mr-1" /> {t('settings.twoFaEnabled')}</>
                    ) : (
                      <><XCircle className="w-3 h-3 mr-1" /> {t('settings.twoFaDisabled')}</>
                    )}
                  </Badge>
                </div>

                {/* Enable 2FA */}
                {!is2FAEnabled && !showSetup && (
                  <Button
                    data-testid="settings-2fa-enable-button"
                    onClick={handleSetup2FA}
                    disabled={setupLoading}
                    className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90"
                  >
                    {setupLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.loading')}</> : <><ShieldCheck className="w-4 h-4 mr-2" /> {t('settings.twoFaSetup')}</>}
                  </Button>
                )}

                {/* Setup Flow */}
                {showSetup && setupData && (
                  <div className="space-y-4 p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                    <h4 className="font-semibold">{t('settings.twoFaSetup')}</h4>
                    
                    {/* QR Code */}
                    <div className="flex justify-center">
                      <img
                        src={setupData.qr_code}
                        alt="2FA QR Code"
                        data-testid="settings-2fa-qr"
                        className="w-48 h-48 rounded-lg bg-white p-2"
                      />
                    </div>
                    
                    <p className="text-sm text-center text-[hsl(var(--muted-foreground))]">
                      {t('settings.twoFaScanQr')}
                    </p>
                    
                    {/* Manual secret */}
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-[hsl(var(--background))] px-3 py-2 rounded flex-1 font-mono break-all">
                        {setupData.secret}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { navigator.clipboard.writeText(setupData.secret); toast.success(t('common.copy')); }}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Verification */}
                    <div className="space-y-2">
                      <Label>{t('settings.twoFaEnterCode')}</Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="settings-2fa-verify-input"
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          className="bg-[hsl(var(--background))] border-[hsl(var(--border))] text-center text-lg tracking-widest"
                        />
                        <Button
                          data-testid="settings-2fa-verify-button"
                          onClick={handleVerify2FA}
                          disabled={verifyLoading || verifyCode.length < 6}
                          className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90"
                        >
                          {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('settings.twoFaVerify')}
                        </Button>
                      </div>
                    </div>
                    
                    <Button variant="ghost" onClick={() => { setShowSetup(false); setSetupData(null); }} className="w-full">
                      {t('common.cancel')}
                    </Button>
                  </div>
                )}

                {/* Backup Codes */}
                {backupCodes.length > 0 && (
                  <Alert className="bg-[rgba(247,200,75,0.08)] border-[#F7C84B]/20">
                    <AlertDescription>
                      <p className="font-semibold mb-2">{t('settings.twoFaBackupCodes')}</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{t('settings.twoFaBackupDesc')}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {backupCodes.map((code, i) => (
                          <code key={i} className="text-sm font-mono bg-[hsl(var(--secondary))] px-3 py-1.5 rounded text-center">
                            {code}
                          </code>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); toast.success(t('common.copy')); }}
                      >
                        <Copy className="w-3 h-3 mr-2" /> {t('common.copy')}
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Disable 2FA */}
                {is2FAEnabled && !showDisable && (
                  <Button
                    data-testid="settings-2fa-disable-button"
                    variant="outline"
                    onClick={() => setShowDisable(true)}
                    className="border-[#D94A4A]/30 text-[#D94A4A] hover:bg-[rgba(217,74,74,0.08)]"
                  >
                    {t('settings.twoFaDisableBtn')}
                  </Button>
                )}

                {showDisable && (
                  <div className="space-y-3 p-4 rounded-lg bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('settings.twoFaDisableDesc')}</p>
                    <div className="flex gap-2">
                      <Input
                        data-testid="settings-2fa-disable-input"
                        value={disableCode}
                        onChange={(e) => setDisableCode(e.target.value)}
                        placeholder="000000"
                        className="bg-[hsl(var(--background))] border-[hsl(var(--border))] text-center tracking-widest"
                      />
                      <Button
                        data-testid="settings-2fa-disable-submit"
                        onClick={handleDisable2FA}
                        disabled={disableLoading || !disableCode}
                        className="bg-[#D94A4A] text-white hover:bg-[#D94A4A]/90"
                      >
                        {disableLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('settings.twoFaDisableBtn')}
                      </Button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setShowDisable(false); setDisableCode(''); }}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                )}

                <Separator className="bg-[hsl(var(--border))]" />

                {/* Password Change */}
                <div className="space-y-2">
                  <Label>{t('settings.changePassword')}</Label>
                  <Input type="password" placeholder={t('settings.currentPassword')} className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                  <Input type="password" placeholder={t('settings.newPassword')} className="bg-[hsl(var(--background))] border-[hsl(var(--border))]" />
                  <Button variant="outline" className="border-[hsl(var(--border))] hover:bg-white/5">
                    {t('settings.updatePassword')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Wallets Tab */}
          <TabsContent value="wallets">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="settings-wallets-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-['Space_Grotesk'] flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-[hsl(var(--primary))]" />
                      {t('settings.walletsTitle')}
                    </CardTitle>
                    <CardDescription className="text-[hsl(var(--muted-foreground))] mt-1">
                      {t('settings.walletsDesc')}
                    </CardDescription>
                  </div>
                  <Button
                    data-testid="settings-connect-wallet-button"
                    onClick={() => setWalletModalOpen(true)}
                    className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2"
                  >
                    <Wallet className="w-4 h-4" />
                    {t('settings.connectWallet')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {walletsLoading ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-[hsl(var(--border))]">
                        <Skeleton className="w-10 h-10 rounded-lg" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                        <Skeleton className="h-8 w-24" />
                      </div>
                    ))}
                  </div>
                ) : connectedWallets.length === 0 ? (
                  <div className="text-center py-12 space-y-4" data-testid="settings-no-wallets">
                    <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--secondary))] flex items-center justify-center mx-auto">
                      <Wallet className="w-8 h-8 text-[hsl(var(--muted-foreground))]" />
                    </div>
                    <div>
                      <p className="font-medium text-[hsl(var(--foreground))]">{t('settings.noWallets')}</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('settings.noWalletsDesc')}</p>
                    </div>
                    <Button
                      data-testid="settings-connect-wallet-empty-button"
                      onClick={() => setWalletModalOpen(true)}
                      className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2"
                    >
                      <Wallet className="w-4 h-4" />
                      {t('settings.connectWallet')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {connectedWallets.map((wallet) => {
                      const chainInfo = CHAIN_INFO[wallet.chain] || { name: wallet.chain, color: '#888', explorer: '#' };
                      return (
                        <div
                          key={wallet.id || wallet.chain}
                          data-testid={`settings-wallet-${wallet.chain}`}
                          className="flex items-center gap-4 p-4 rounded-xl border border-[hsl(var(--border))] hover:bg-white/[0.02] transition-colors duration-200"
                        >
                          {/* Chain Icon */}
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${chainInfo.color}15` }}
                          >
                            <Wallet className="w-5 h-5" style={{ color: chainInfo.color }} />
                          </div>

                          {/* Wallet Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{chainInfo.name}</span>
                              <Badge className="text-[10px] h-4 bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20">
                                <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                                {t('settings.walletVerified')}
                              </Badge>
                              {wallet.wallet_type && (
                                <Badge className="text-[10px] h-4 bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">
                                  {wallet.wallet_type}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <code className="text-xs font-mono text-[hsl(var(--muted-foreground))]">
                                {formatAddress(wallet.wallet_address)}
                              </code>
                              <button
                                onClick={() => { navigator.clipboard.writeText(wallet.wallet_address); toast.success(t('common.copy')); }}
                                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors duration-200"
                                data-testid={`settings-wallet-copy-${wallet.chain}`}
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                              <a
                                href={`${chainInfo.explorer}${wallet.wallet_address}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors duration-200"
                                data-testid={`settings-wallet-explorer-${wallet.chain}`}
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                            {wallet.linked_at && (
                              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                                {t('settings.walletLinkedAt')}: {formatDate(wallet.linked_at)}
                              </p>
                            )}
                          </div>

                          {/* Unlink Button */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`settings-wallet-unlink-${wallet.chain}`}
                                disabled={unlinkingChain === wallet.chain}
                                className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A4A] hover:bg-[rgba(217,74,74,0.08)] gap-1.5"
                              >
                                {unlinkingChain === wallet.chain ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Unplug className="w-3.5 h-3.5" />
                                )}
                                {t('settings.unlinkWallet')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-['Space_Grotesk']">{t('settings.unlinkWallet')}</AlertDialogTitle>
                                <AlertDialogDescription className="text-[hsl(var(--muted-foreground))]">
                                  {t('settings.unlinkConfirm')}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-[hsl(var(--border))] hover:bg-white/5">
                                  {t('common.cancel')}
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleUnlinkWallet(wallet.chain)}
                                  className="bg-[#D94A4A] text-white hover:bg-[#D94A4A]/90"
                                >
                                  {t('settings.unlinkWallet')}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="font-['Space_Grotesk']">{t('settings.preferencesTitle')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('settings.emailNotifications')}</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('settings.emailNotificationsDesc')}</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <Separator className="bg-[hsl(var(--border))]" />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('settings.priceAlerts')}</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('settings.priceAlertsDesc')}</p>
                  </div>
                  <Switch />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <ConnectWalletModal
        open={walletModalOpen}
        onOpenChange={setWalletModalOpen}
        onWalletConnected={handleWalletLinked}
        mode="link"
      />
    </DashboardLayout>
  );
}
